import React, { ChangeEvent, useEffect, useState } from 'react';

import AlertDialog from '../../shared/components/AlertDialog';
import Button from '@mui/material/Button';
import { CustomEmailOptions } from '../../shared/models/CustomEmailOptions';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import CustomNotificationSetting from '../../shared/components/CustomNotificationSetting';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import { NotificationOptionsEnum } from '../models/NotificationOptions';
import Overlay from '../../shared/components/Overlay';
import Radio from '@mui/material/Radio';
import { RadioGroup } from '@mui/material';
import { SUPPORT_MESSAGE } from '../../shared/constants';
import Typography from '@mui/material/Typography';
import { UserOptionAssets } from '../../shared/models/UserOption';
import styles from '../styles/NotificationSettings.module.css';
import { useNotificationSettings } from '../hooks/useNotificationSettings';
import { useTheme } from '@mui/material';

interface NotificationSettingsDialogProps {
  open: boolean;
  userkey: number;
  onClose: () => void;
}
export default function NotificationSettingsDialog(
  props: NotificationSettingsDialogProps
) {
  const muiTheme = useTheme();
  const secondaryColor = muiTheme.palette.secondary.main;
  const {
    optionsLoading,
    userOptions,
    updatingSettings,
    updateData,
    updateError,
    updateNotificationSetting
  } = useNotificationSettings(props.userkey);

  const [selectedSetting, setSelectedSetting] =
    useState<NotificationOptionsEnum>(null);

  const [selectedCustomOptions, setSelectedCustomOptions] =
    useState<CustomEmailOptions>(null);

  useEffect(() => {
    if (userOptions) {
      setSelectedSetting(userOptions.assets.notification);
      setSelectedCustomOptions(userOptions.assets.emailOptions);
    }
  }, [userOptions]);

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    const notificationOption = +event.target.value as NotificationOptionsEnum;
    setSelectedSetting(notificationOption);
  };

  const handleSave = () => {
    const notificationOption = {
      ...userOptions.assets,
      notification: selectedSetting,
      emailOptions: selectedCustomOptions
    } as UserOptionAssets;
    updateNotificationSetting(props.userkey, notificationOption);
  };

  const notificationOptions = [
    {
      value: NotificationOptionsEnum.None,
      title: 'Unsubscribed',
      subtitle:
        'You will never receive automatic and custom notifications sent to your email address.'
    },
    {
      value: NotificationOptionsEnum.All,
      title: 'Subscribed',
      subtitle: 'You will receive all notifications sent to your email address.'
    },
    {
      value: NotificationOptionsEnum.Custom,
      title: 'Custom',
      subtitle: 'You will only receive the notifications of your choosing.'
    }
  ];

  return (
    <>
      {optionsLoading && <Overlay spinner message="Loading your settings..." />}
      <Dialog open={props.open} fullWidth maxWidth="sm">
        <DialogTitle>External notification settings</DialogTitle>
        <DialogContent>
          <RadioGroup name={'notification-setting'} value={selectedSetting}>
            {notificationOptions.map(({ value, title, subtitle }, index) => (
              <div key={`option-${index}`}>
                <FormControlLabel
                  value={value}
                  control={<Radio color="primary" />}
                  label={<Typography variant="subtitle2">{title}</Typography>}
                  onChange={handleChange}
                />
                <Typography className={styles.optionTitle} variant="body2">
                  {subtitle}
                </Typography>
              </div>
            ))}
          </RadioGroup>
          {selectedSetting === NotificationOptionsEnum.Custom && (
            <div className={`${styles.optionTitle} ${styles.optionContainer}`}>
              <CustomNotificationSetting
                emailOptions={userOptions.assets.emailOptions}
                handleChange={setSelectedCustomOptions}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => props.onClose()}
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave}>
            {updatingSettings ? (
              <>
                <CustomLoadingProgress color={secondaryColor} size={14} />{' '}
                Saving
              </>
            ) : (
              'Save'
            )}
            {updateData && (
              <AlertDialog
                title={'Success!'}
                message="Notification settings has been updated successfully."
                onClose={() => {
                  props.onClose();
                }}
              />
            )}
            {updateError && (
              <AlertDialog
                title={'Updating notification settings has failed.'}
                message={SUPPORT_MESSAGE}
                onClose={() => {
                  props.onClose();
                }}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
